import React from 'react';
import styled from 'styled-components';

import {
  FormGroup,
  FormHelpText,
  FormInput as EncoreFormInput,
  FormSelect,
  Type,
} from '@spotify-internal/encore-creator-web';
import { gray10 } from '@spotify-internal/encore-foundation';

import {
  useViewport,
  Viewport,
} from '../../../../../../shared/lib/useViewport';
import {
  musicReliefOrgDisplayNamesWithCountries,
  musicReliefOrgLinks,
} from '../../../lib/constants';
import { unreachable } from '../../../lib/unreachable';
import { DialogBodyProps, Org } from '../../../types';

const FormHelpTextFontSize14 = styled(FormHelpText)`
  font-size: 14px;
  line-height: 20px;
`;

const LabelSpan16 = styled(Type).attrs({
  color: gray10,
  variant: Type.body2,
})`
  font-size: 16px;
  line-height: 24px;
  font-weight: ${Type.bold};
`;

const options = {
  cashapp: {
    type: 'text',
    displayName: 'Cash App',
  },
  gofundme: {
    type: 'text',
    displayName: 'GoFundMe',
  },
  paypal: {
    type: 'text',
    displayName: 'PayPal',
  },
  musicrelieforg: {
    type: 'select',
    displayName: 'Music Relief organization',
  },
} as const;

const FauxPlaceholder = styled(FormHelpTextFontSize14)`
  font-size: 16px;
  line-height: 24px;
  position: absolute;
  white-space: nowrap;
  top: 38px;

  /* @supports (-webkit-touch-callout: none) {
    top: 38px;
  } */
`;

const FauxPlaceholderCondensed = styled(FauxPlaceholder)`
  top: 26px;

  /* @supports (-webkit-touch-callout: none) {
    top: 26px;
  } */
`;

const FauxPlaceholderPaypal = styled(FauxPlaceholder)`
  left: 192px;
`;

const FauxPlaceholderPaypalCondensed = styled(FauxPlaceholderCondensed)`
  left: 180px;

  @supports (-webkit-touch-callout: none) {
    left: 187px;
  }
`;

const FauxPlaceholderGofundme = styled(FauxPlaceholder)`
  left: 239px;
`;

const FauxPlaceholderGofundmeCondensed = styled(FauxPlaceholderCondensed)`
  left: 227px;

  @supports (-webkit-touch-callout: none) {
    left: 233px;
  }
`;

const FauxPlaceholderCash = styled(FauxPlaceholder)`
  left: 149px;
`;

const FauxPlaceholderCashCondensed = styled(FauxPlaceholderCondensed)`
  left: 138px;

  @supports (-webkit-touch-callout: none) {
    left: 144px;
  }
`;

const FormInputPreventAutoZoomIOS = styled(EncoreFormInput)`
  /* font-size of at least 16px is required in order to prevent triggering
    awkward auto-zoom accessibility feature in iOS */
  font-size: 16px;
`;

const FormSelectPreventAutoZoomIOS = styled(FormSelect)`
  /* font-size of at least 16px is required in order to prevent triggering
    awkward auto-zoom accessibility feature in iOS */
  font-size: 16px;
`;

const FormGroupPositionRelative = styled(FormGroup)`
  position: relative;
`;

export function DEPRECATEDInputGroup(props: DialogBodyProps) {
  const viewport = useViewport();

  const condensed = viewport === Viewport.XS;

  const [touched, setTouched] = React.useState(false);

  const textInputRef = React.useRef<HTMLInputElement>(null);

  const isPaypal = props.provider.value === 'paypal';

  const isGofundme = props.provider.value === 'gofundme';

  const isCash = props.provider.value === 'cashapp';

  // @ts-ignore
  const type: 'text' | 'select' = options[props.provider.value]?.type ?? 'text';

  function onOrgChange(ev: React.ChangeEvent<HTMLInputElement>) {
    if (ev.target.value) {
      props.org.setValue(ev);
      props.link.setInnerValue(musicReliefOrgLinks[ev.target.value as Org]);
    }
  }

  let fauxPlaceholder = null;

  function onClickPlaceholder() {
    textInputRef.current?.focus();
  }

  function onPasteTextInput(evt: React.ClipboardEvent) {
    const pastedText = (
      evt.clipboardData || (window as any).clipboardData
    ).getData('text/plain');

    props.link.setValue({ target: { value: pastedText } } as any);

    // Important! We don't want the onChange handler to handle this again
    evt.preventDefault();
  }

  const placeholderCopyCash = 'yourcashtag';

  const placeholderCopyPaypal = 'your-user-name';

  const placeholderCopyGofundme = 'campaign';

  if (!touched && props.link.value === props.link.initialValue) {
    if (isPaypal) {
      fauxPlaceholder = (
        <FauxPlaceholderPaypal onClick={onClickPlaceholder}>
          {placeholderCopyPaypal}
        </FauxPlaceholderPaypal>
      );
      if (condensed) {
        fauxPlaceholder = (
          <FauxPlaceholderPaypalCondensed onClick={onClickPlaceholder}>
            {placeholderCopyPaypal}
          </FauxPlaceholderPaypalCondensed>
        );
      }
    } else if (isGofundme) {
      fauxPlaceholder = (
        <FauxPlaceholderGofundme onClick={onClickPlaceholder}>
          {placeholderCopyGofundme}
        </FauxPlaceholderGofundme>
      );
      if (condensed) {
        fauxPlaceholder = (
          <FauxPlaceholderGofundmeCondensed onClick={onClickPlaceholder}>
            {placeholderCopyGofundme}
          </FauxPlaceholderGofundmeCondensed>
        );
      }
    } else if (isCash) {
      fauxPlaceholder = (
        <FauxPlaceholderCash onClick={onClickPlaceholder}>
          {placeholderCopyCash}
        </FauxPlaceholderCash>
      );
      if (condensed) {
        fauxPlaceholder = (
          <FauxPlaceholderCashCondensed onClick={onClickPlaceholder}>
            {placeholderCopyCash}
          </FauxPlaceholderCashCondensed>
        );
      }
    }
  }

  switch (type) {
    case 'text': {
      const hasErrors = props.link.errors.length > 0;

      return (
        <FormGroupPositionRelative
          label={<LabelSpan16>Add your fundraising link</LabelSpan16>}
          labelFor="fundraising-pick-text-input"
        >
          <div>
            <FormInputPreventAutoZoomIOS
              id="fundraising-pick-text-input"
              type="text"
              ref={textInputRef}
              onChange={props.link.setValue}
              onFocus={() => setTouched(true)}
              value={props.link.value}
              error={hasErrors}
              aria-invalid={hasErrors}
              onPaste={onPasteTextInput}
              aria-describedby={
                hasErrors ? 'fundraising-pick-text-input-error-0' : undefined
              }
            />

            {fauxPlaceholder}
          </div>

          {props.link.errors?.map((e, i) => (
            <FormHelpTextFontSize14
              id={`fundraising-pick-text-input-error-${i}`}
              key={i}
              error
            >
              {e}
            </FormHelpTextFontSize14>
          )) ?? null}
          {!hasErrors && (
            <FormHelpTextFontSize14>
              <Type as="p" variant={Type.body2}>
                Make sure your link is correct. You won't be able to edit this
                after you submit.
              </Type>
            </FormHelpTextFontSize14>
          )}
        </FormGroupPositionRelative>
      );
    }
    case 'select': {
      const hasErrors = props.link.errors.length > 0;

      return (
        <FormGroup
          label={<LabelSpan16>Choose Music Relief organization</LabelSpan16>}
          labelFor="fundraising-pick-select-input"
        >
          <FormSelectPreventAutoZoomIOS
            id="fundraising-pick-select-input"
            onChange={onOrgChange}
            value={props.org.value ?? ''}
            error={hasErrors}
            aria-invalid={hasErrors}
            aria-describedby={
              hasErrors ? 'fundraising-pick-select-input-error-0' : undefined
            }
          >
            <option value="">Please select</option>
            {Object.keys(musicReliefOrgDisplayNamesWithCountries).map(
              orgName => {
                const orgDisplayName =
                  // @ts-ignore
                  musicReliefOrgDisplayNamesWithCountries[orgName];
                return (
                  <option key={orgName} value={orgName}>
                    {orgDisplayName}
                  </option>
                );
              },
            )}
          </FormSelectPreventAutoZoomIOS>
          {props.link.errors?.map((e, i) => (
            <FormHelpTextFontSize14
              id={`fundraising-pick-select-input-error-${i}`}
              key={i}
              error
            >
              {e}
            </FormHelpTextFontSize14>
          )) ?? null}
        </FormGroup>
      );
    }
    default:
      unreachable(type);
      return null;
  }
}
